import React, { useState, useEffect } from 'react';
import './EditSleepEntry.css';

interface SleepEntry {
  id: string;
  baby_id: string;
  start_time: string;
  end_time: string | null;
  created_at: string;
  is_ongoing: boolean;
  duration_minutes: number | null;
}

interface EditSleepEntryProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (sleepId: string, startTime: Date, endTime: Date) => void;
  onDelete: (sleepId: string) => void;
  sleepEntry: SleepEntry | null;
  use24h?: boolean;
}

const EditSleepEntry: React.FC<EditSleepEntryProps> = ({
  isOpen,
  onClose,
  onUpdate,
  onDelete,
  sleepEntry,
  use24h = false,
}) => {
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState('');

  // Initialize form with sleep entry data when opened
  useEffect(() => {
    if (isOpen && sleepEntry) {
      // Parse start time (backend now returns with Z suffix for UTC)
      const startDateTime = new Date(sleepEntry.start_time);
      const startDateStr = startDateTime.toISOString().split('T')[0]; // YYYY-MM-DD
      const startTimeStr = startDateTime.toTimeString().slice(0, 5); // HH:MM

      setStartDate(startDateStr);
      setStartTime(startTimeStr);

      // Parse end time if it exists
      if (sleepEntry.end_time) {
        const endDateTime = new Date(sleepEntry.end_time);
        const endDateStr = endDateTime.toISOString().split('T')[0];
        const endTimeStr = endDateTime.toTimeString().slice(0, 5);

        setEndDate(endDateStr);
        setEndTime(endTimeStr);
      } else {
        // If ongoing, set end time to current time
        const now = new Date();
        const endDateStr = now.toISOString().split('T')[0];
        const endTimeStr = now.toTimeString().slice(0, 5);

        setEndDate(endDateStr);
        setEndTime(endTimeStr);
      }
      
      setError('');
      setShowDeleteConfirm(false);
    }
  }, [isOpen, sleepEntry]);

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!startDate || !startTime || !endDate || !endTime || !sleepEntry) {
      setError('Please fill in all fields');
      return;
    }

    // Create Date objects
    const startDateTime = new Date(`${startDate}T${startTime}`);
    const endDateTime = new Date(`${endDate}T${endTime}`);

    // Validation
    if (startDateTime >= endDateTime) {
      setError('End time must be after start time');
      return;
    }

    const now = new Date();
    if (startDateTime > now) {
      setError('Start time cannot be in the future');
      return;
    }

    if (endDateTime > now) {
      setError('End time cannot be in the future');
      return;
    }

    // Check if sleep duration is reasonable (not more than 24 hours)
    const durationHours = (endDateTime.getTime() - startDateTime.getTime()) / (1000 * 60 * 60);
    if (durationHours > 24) {
      setError('Sleep duration cannot be more than 24 hours');
      return;
    }

    setIsSubmitting(true);
    try {
      await onUpdate(sleepEntry.id, startDateTime, endDateTime);
      onClose();
    } catch (err) {
      setError('Failed to update sleep entry');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!sleepEntry) return;
    
    setIsSubmitting(true);
    try {
      await onDelete(sleepEntry.id);
      onClose();
    } catch (err) {
      setError('Failed to delete sleep entry');
    } finally {
      setIsSubmitting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen || !sleepEntry) return null;

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="edit-sleep-modal">
        <div className="modal-header">
          <h3>Edit Sleep Entry</h3>
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        {showDeleteConfirm ? (
          <div className="delete-confirm">
            <h4>Delete Sleep Entry?</h4>
            <p>This action cannot be undone.</p>
            <div className="confirm-actions">
              <button 
                onClick={() => setShowDeleteConfirm(false)} 
                className="cancel-btn"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button 
                onClick={handleDelete} 
                className="delete-confirm-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleUpdate} className="edit-sleep-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="edit-start-date">Start Date</label>
                <input
                  type="date"
                  id="edit-start-date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-start-time">Start Time</label>
                <input
                  type="time"
                  id="edit-start-time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="edit-end-date">End Date</label>
                <input
                  type="date"
                  id="edit-end-date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-end-time">End Time</label>
                <input
                  type="time"
                  id="edit-end-time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  required
                />
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="form-actions">
              <button 
                type="button" 
                onClick={() => setShowDeleteConfirm(true)} 
                className="delete-btn"
                disabled={isSubmitting}
              >
                Delete
              </button>
              <div className="right-actions">
                <button type="button" onClick={onClose} className="cancel-btn">
                  Cancel
                </button>
                <button type="submit" disabled={isSubmitting} className="update-btn">
                  {isSubmitting ? 'Updating...' : 'Update'}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default EditSleepEntry;